import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Prompt } from "@/shared/ui";
import { useOrganizationForm } from "../hooks";
import {
  OrganizationBankSection,
  OrganizationContactSection,
  OrganizationDirectorSection,
  OrganizationPageSkeleton,
  OrganizationPhotoCard,
  OrganizationReadOnlySection,
} from "../components";

export default function MyOrganizationPage() {
  const { t, i18n } = useTranslation("MyOrganizationPage");
  const locale = (i18n.language as "ru" | "kk" | "en") ?? "ru";
  const form = useOrganizationForm();

  useEffect(
    function guardTabClose() {
      function onBeforeUnload(event: BeforeUnloadEvent) {
        if (!form.isDirty) return;
        event.preventDefault();
        event.returnValue = "";
      }
      window.addEventListener("beforeunload", onBeforeUnload);
      return function cleanup() {
        window.removeEventListener("beforeunload", onBeforeUnload);
      };
    },
    [form.isDirty],
  );

  if (form.isLoading && !form.data) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <meta name="description" content={t("meta.description")} />
        <div className="flex items-center justify-between pb-4 border-b surface-app-stroke mb-5">
          <h1 className="text-display-xs content-base-primary">{t("page.title")}</h1>
        </div>
        <OrganizationPageSkeleton />
      </>
    );
  }

  if (form.error || !form.data) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <meta name="description" content={t("meta.description")} />
        <div className="flex items-center justify-between pb-4 border-b surface-app-stroke mb-5">
          <h1 className="text-display-xs content-base-primary">{t("page.title")}</h1>
        </div>
        <p className="text-body-regular-md content-base-secondary">{t("page.errorLoading")}</p>
      </>
    );
  }

  const data = form.data;
  const readonlyLabels = {
    name: t("fields.name"),
    bin: t("fields.bin"),
    registrationNumber: t("fields.registrationNumber"),
    organizationType: t("fields.organizationType"),
    registrationDate: t("fields.registrationDate"),
    chiefName: t("fields.chiefName"),
    createdAt: t("fields.createdAt"),
  };

  const photoLabels = {
    title: t("sections.photo.title"),
    hint: t("photo.hint"),
    upload: t("photo.upload"),
    replace: t("photo.replace"),
    delete: t("photo.delete"),
    cancel: t("photo.cancel"),
    empty: t("photo.empty"),
  };

  const saveDisabled = !form.isDirty || form.isMutating;

  return (
    <>
      <title>{t("meta.title")}</title>
      <meta name="description" content={t("meta.description")} />
      <div className="flex items-center justify-between pb-4 border-b surface-app-stroke mb-5">
        <h1 className="text-display-xs content-base-primary">{t("page.title")}</h1>
      </div>
      <form onSubmit={form.submit} className="flex flex-col">
        <div className="flex flex-col gap-7">
          <OrganizationPhotoCard
            existingUrl={form.photo.existingUrl}
            pendingFile={form.photo.pendingFile}
            markedForDelete={form.photo.markedForDelete}
            fileError={form.photo.fileError}
            disabled={form.isMutating}
            labels={photoLabels}
            onFileSelected={form.photo.onFileSelected}
            onClearPending={form.photo.onClearPending}
            onMarkDelete={form.photo.onMarkDelete}
            onCancelDelete={form.photo.onCancelDelete}
            onImageError={form.photo.onImageError}
          />
          <OrganizationReadOnlySection
            data={data}
            locale={locale}
            title={t("sections.readonly.title")}
            labels={readonlyLabels}
          />
          <OrganizationContactSection
            register={form.register}
            watch={form.watch}
            setValue={form.setValue}
            errors={form.errors}
            t={t}
            disabled={form.isMutating}
            emailTakenMessage={form.emailTakenMessage}
          />
          <OrganizationBankSection
            register={form.register}
            errors={form.errors}
            t={t}
            disabled={form.isMutating}
          />
          <OrganizationDirectorSection
            register={form.register}
            errors={form.errors}
            t={t}
            disabled={form.isMutating}
          />
        </div>
        <div className="flex justify-end mt-4">
          <Button
            variant="primary"
            size="huge"
            type="submit"
            disabled={saveDisabled}>
            {t("actions.save")}
          </Button>
        </div>
      </form>

      {form.saveState.status === "saving" && (
        <Prompt
          loaderMode
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("save.loading")}
          additionalText={t("save.loadingAdditional")}
          namespace="MyOrganizationPage"
        />
      )}

      {form.saveState.status === "success" && (
        <Prompt
          variant="success"
          title={t("save.success.title")}
          text={t("save.success.text")}
          onClose={form.dismissSaveState}
          namespace="MyOrganizationPage"
        />
      )}

      {form.saveState.status === "error" && (
        <Prompt
          variant="error"
          title={t("save.error.title")}
          text={form.saveState.message ?? t("save.error.text")}
          onClose={form.dismissSaveState}
          namespace="MyOrganizationPage"
        />
      )}
    </>
  );
}
